import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { LoyaltyService } from 'src/app/services/loyalty.service';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-chain-loyalty',
  templateUrl: './chain-loyalty.component.html',
  styleUrls: ['./chain-loyalty.component.scss']
})
export class ChainLoyaltyComponent implements OnInit {

  chainId: string = '';
  activeTab: string = 'config';

  // ----- Config -----
  loyaltyConfig: any = null;
  configLoading: boolean = false;
  configSaving: boolean = false;
  isEnabled: boolean = false;
  birthdayDiscountRate: number = 10;
  tiers: any[] = [];
  promotionRules: any[] = [];

  // ----- Members -----
  members: any[] = [];
  membersLoading: boolean = false;
  memberSearch: string = '';
  memberTierFilter: string = '';
  memberActiveFilter: string = 'all';
  membersPage: number = 1;
  membersLimit: number = 20;
  membersTotal: number = 0;

  showAddMemberDialog: boolean = false;
  addMemberForm!: FormGroup;
  addMemberSubmitting: boolean = false;
  userSearchResults: any[] = [];
  userSearchLoading: boolean = false;

  editingMembership: any = null;
  showEditMemberDialog: boolean = false;
  editMemberForm!: FormGroup;
  editMemberSubmitting: boolean = false;

  // ----- Vouchers -----
  vouchers: any[] = [];
  vouchersLoading: boolean = false;

  showVoucherDialog: boolean = false;
  editingVoucher: any = null;
  voucherForm!: FormGroup;
  voucherSubmitting: boolean = false;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private loyaltyService: LoyaltyService,
    private spinner: NgxSpinnerService,
    private toastr: ToastrService
  ) {}

  ngOnInit(): void {
    this.chainId = this.route.snapshot.params['chainId'] || '';
    this.initForms();
    if (this.chainId) {
      this.loadConfig();
      this.loadMembers();
      this.loadVouchers();
    }
  }

  setTab(tab: string): void {
    this.activeTab = tab;
  }

  goBack(): void {
    this.router.navigate(['/my-chains']);
  }

  // ======================== Config ========================

  loadConfig(): void {
    this.configLoading = true;
    this.loyaltyService.getChainConfig(this.chainId).subscribe({
      next: (res: any) => {
        this.configLoading = false;
        const data = res.data || res.response?.data || null;
        if (data) {
          this.loyaltyConfig = data;
          this.isEnabled = data.isEnabled || false;
          this.birthdayDiscountRate = data.birthdayDiscountRate ?? 10;
          this.tiers = data.tiers ? JSON.parse(JSON.stringify(data.tiers)) : [];
          this.promotionRules = data.promotionRules ? JSON.parse(JSON.stringify(data.promotionRules)) : [];
        } else {
          this.tiers = [];
          this.promotionRules = [];
        }
      },
      error: (err: any) => {
        this.configLoading = false;
        if (err.status !== 404) {
          this.toastr.error(err.error?.response?.message || 'Failed to load loyalty config', 'Error');
        }
        this.tiers = [];
        this.promotionRules = [];
      }
    });
  }

  addTier(): void {
    this.tiers.push({
      tierKey: '',
      label: '',
      discountRate: 5,
      minCartAmount: 0,
      firstPurchaseBonusRate: 0
    });
  }

  removeTier(index: number): void {
    this.tiers.splice(index, 1);
  }

  onTierLabelChange(tier: any): void {
    if (tier.label) {
      tier.tierKey = tier.label
        .toLowerCase()
        .trim()
        .replace(/[^\w\s]/g, '')
        .replace(/\s+/g, '_');
    } else {
      tier.tierKey = '';
    }
  }

  addPromotionRule(): void {
    const firstTierKey = this.tiers.length > 0 ? this.tiers[0].tierKey : '';
    this.promotionRules.push({
      tierKey: firstTierKey,
      requiredSpend: 0,
      requiredVisits: 0
    });
  }

  removePromotionRule(index: number): void {
    this.promotionRules.splice(index, 1);
  }

  saveConfig(): void {
    if (this.tiers.some(t => !t.tierKey || !t.tierKey.trim() || !t.label || !t.label.trim())) {
      this.toastr.error('All tiers must have a non-empty label and key. Fill in the tier label first.', 'Validation');
      return;
    }
    if (this.promotionRules.some(r => !r.tierKey || !r.tierKey.trim())) {
      this.toastr.error('All promotion rules must have a tier selected.', 'Validation');
      return;
    }
    this.configSaving = true;
    this.spinner.show();
    const payload = {
      isEnabled: this.isEnabled,
      birthdayDiscountRate: this.birthdayDiscountRate,
      tiers: this.tiers,
      promotionRules: this.promotionRules
    };
    this.loyaltyService.updateChainConfig(this.chainId, payload).subscribe({
      next: (res: any) => {
        this.configSaving = false;
        this.spinner.hide();
        const data = res.data || res.response?.data;
        if (data) {
          this.loyaltyConfig = data;
          this.tiers = JSON.parse(JSON.stringify(data.tiers || []));
          this.promotionRules = JSON.parse(JSON.stringify(data.promotionRules || []));
        }
        this.toastr.success('Chain loyalty configuration saved', 'Saved');
      },
      error: (err: any) => {
        this.configSaving = false;
        this.spinner.hide();
        this.toastr.error(err.error?.response?.message || 'Failed to save loyalty config', 'Error');
      }
    });
  }

  // ======================== Members ========================

  loadMembers(): void {
    this.membersLoading = true;
    const params: any = {
      page: this.membersPage,
      limit: this.membersLimit
    };
    if (this.memberSearch) params.search = this.memberSearch;
    if (this.memberTierFilter) params.tier = this.memberTierFilter;
    if (this.memberActiveFilter === 'active') params.active = true;
    if (this.memberActiveFilter === 'inactive') params.active = false;

    this.loyaltyService.getChainMembers(this.chainId, params).subscribe({
      next: (res: any) => {
        this.membersLoading = false;
        this.members = res.response?.data?.members || res.data?.members || [];
        this.membersTotal = res.response?.data?.pagination?.total || res.data?.pagination?.total || 0;
        this.membersPage = res.response?.data?.pagination?.page || res.data?.pagination?.page || 1;
      },
      error: (err: any) => {
        this.membersLoading = false;
        this.toastr.error(err.error?.response?.message || 'Failed to load members', 'Error');
        this.members = [];
      }
    });
  }

  onMemberSearchChange(): void {
    this.membersPage = 1;
    this.loadMembers();
  }

  onMemberPageChange(page: number): void {
    this.membersPage = page;
    this.loadMembers();
  }

  get membersTotalPages(): number {
    return Math.ceil(this.membersTotal / this.membersLimit) || 1;
  }

  getMemberPages(): number[] {
    const total = this.membersTotalPages;
    const current = this.membersPage;
    const delta = 2;
    const start = Math.max(1, current - delta);
    const end = Math.min(total, current + delta);
    const pages: number[] = [];
    for (let i = start; i <= end; i++) pages.push(i);
    return pages;
  }

  openAddMemberDialog(): void {
    this.userSearchResults = [];
    this.addMemberForm.reset();
    this.addMemberForm.patchValue({ tierKey: this.tiers[0]?.tierKey || '' });
    this.showAddMemberDialog = true;
  }

  closeAddMemberDialog(): void {
    this.showAddMemberDialog = false;
  }

  onUserSearchInput(): void {
    this.addMemberForm.patchValue({ userId: '' });
    this.userSearchResults = [];
  }

  searchUsers(): void {
    const q = this.addMemberForm.get('userSearch')?.value?.trim();
    if (!q || q.length < 2) return;
    this.userSearchLoading = true;
    this.loyaltyService.searchUsers(q).subscribe({
      next: (res: any) => {
        this.userSearchLoading = false;
        this.userSearchResults = res.response?.data?.userData || res.data?.userData || [];
      },
      error: () => {
        this.userSearchLoading = false;
        this.userSearchResults = [];
      }
    });
  }

  selectUser(user: any): void {
    this.addMemberForm.patchValue({ userId: user._id, userSearch: user.name || user.email });
    this.userSearchResults = [];
  }

  submitAddMember(): void {
    if (this.addMemberForm.invalid) {
      this.addMemberForm.markAllAsTouched();
      return;
    }
    this.addMemberSubmitting = true;
    const val = this.addMemberForm.value;
    const payload: any = { userId: val.userId, tierKey: val.tierKey };
    if (val.expiresAt) payload.expiresAt = new Date(val.expiresAt + 'T23:59:59').toISOString();

    this.loyaltyService.addChainMember(this.chainId, payload).subscribe({
      next: () => {
        this.addMemberSubmitting = false;
        this.toastr.success('Member added successfully', 'Added');
        this.closeAddMemberDialog();
        this.loadMembers();
      },
      error: (err: any) => {
        this.addMemberSubmitting = false;
        this.toastr.error(err.error?.response?.message || 'Failed to add member', 'Error');
      }
    });
  }

  openEditMemberDialog(membership: any): void {
    this.editingMembership = membership;
    this.editMemberForm.patchValue({
      tierKey: membership.tierKey,
      expiresAt: membership.expiresAt ? membership.expiresAt.split('T')[0] : '',
      isActive: membership.isActive
    });
    this.showEditMemberDialog = true;
  }

  closeEditMemberDialog(): void {
    this.showEditMemberDialog = false;
    this.editingMembership = null;
  }

  submitEditMember(): void {
    if (this.editMemberForm.invalid) return;
    this.editMemberSubmitting = true;
    const val = this.editMemberForm.value;
    const payload: any = {
      tierKey: val.tierKey,
      isActive: val.isActive
    };
    if (val.expiresAt) {
      payload.expiresAt = new Date(val.expiresAt + 'T23:59:59').toISOString();
    } else {
      payload.expiresAt = null;
    }
    this.loyaltyService.updateChainMember(this.chainId, this.editingMembership._id, payload).subscribe({
      next: () => {
        this.editMemberSubmitting = false;
        this.toastr.success('Membership updated', 'Updated');
        this.closeEditMemberDialog();
        this.loadMembers();
      },
      error: (err: any) => {
        this.editMemberSubmitting = false;
        this.toastr.error(err.error?.response?.message || 'Failed to update membership', 'Error');
      }
    });
  }

  toggleMemberActive(membership: any): void {
    const newActive = !membership.isActive;
    this.loyaltyService.updateChainMember(this.chainId, membership._id, { isActive: newActive }).subscribe({
      next: () => {
        membership.isActive = newActive;
        this.toastr.success(newActive ? 'Member reactivated' : 'Member deactivated', 'Updated');
      },
      error: (err: any) => {
        this.toastr.error(err.error?.response?.message || 'Failed to update member status', 'Error');
      }
    });
  }

  deleteMember(membershipId: string): void {
    Swal.fire({
      title: 'Remove membership?',
      text: 'This will permanently remove the chain loyalty membership.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, remove it',
      confirmButtonColor: '#dc3545'
    }).then(result => {
      if (result.isConfirmed) {
        this.loyaltyService.deleteChainMember(this.chainId, membershipId).subscribe({
          next: () => {
            this.toastr.success('Membership removed', 'Removed');
            this.loadMembers();
          },
          error: (err: any) => {
            this.toastr.error(err.error?.response?.message || 'Failed to remove membership', 'Error');
          }
        });
      }
    });
  }

  getTierLabel(tierKey: string): string {
    const tier = this.tiers.find(t => t.tierKey === tierKey);
    return tier?.label || tierKey;
  }

  getTierBadgeClass(tierKey: string): string {
    const map: { [key: string]: string } = {
      'member': 'bg-primary',
      'co_owner': 'bg-success',
      'vip': 'bg-warning text-dark',
      'premium': 'bg-info text-dark'
    };
    return map[tierKey] || 'bg-secondary';
  }

  // ======================== Vouchers ========================

  loadVouchers(): void {
    this.vouchersLoading = true;
    this.loyaltyService.getChainVouchers(this.chainId).subscribe({
      next: (res: any) => {
        this.vouchersLoading = false;
        this.vouchers = res.response?.data?.vouchers || res.data?.vouchers || res.response?.data || res.data || [];
      },
      error: (err: any) => {
        this.vouchersLoading = false;
        if (err.status !== 404) {
          this.toastr.error(err.error?.response?.message || 'Failed to load vouchers', 'Error');
        }
        this.vouchers = [];
      }
    });
  }

  openCreateVoucherDialog(): void {
    this.editingVoucher = null;
    this.voucherForm.reset();
    this.voucherForm.patchValue({
      discountType: 'percentage',
      value: 10,
      usageLimit: 100,
      isActive: true
    });
    this.showVoucherDialog = true;
  }

  openEditVoucherDialog(voucher: any): void {
    this.editingVoucher = voucher;
    this.voucherForm.patchValue({
      code: voucher.code,
      discountType: voucher.discountType,
      value: voucher.value,
      usageLimit: voucher.usageLimit,
      expiresAt: voucher.expiresAt ? voucher.expiresAt.split('T')[0] : '',
      description: voucher.description || '',
      isActive: voucher.isActive
    });
    this.showVoucherDialog = true;
  }

  closeVoucherDialog(): void {
    this.showVoucherDialog = false;
    this.editingVoucher = null;
  }

  submitVoucher(): void {
    if (this.voucherForm.invalid) {
      this.voucherForm.markAllAsTouched();
      return;
    }
    this.voucherSubmitting = true;
    const val = this.voucherForm.value;

    if (this.editingVoucher) {
      const payload: any = { isActive: val.isActive, usageLimit: val.usageLimit };
      if (val.expiresAt) {
        payload.expiresAt = new Date(val.expiresAt + 'T23:59:59').toISOString();
      } else {
        payload.expiresAt = null;
      }
      this.loyaltyService.updateChainVoucher(this.chainId, this.editingVoucher._id, payload).subscribe({
        next: () => {
          this.voucherSubmitting = false;
          this.toastr.success('Voucher updated', 'Updated');
          this.closeVoucherDialog();
          this.loadVouchers();
        },
        error: (err: any) => {
          this.voucherSubmitting = false;
          this.toastr.error(err.error?.response?.message || 'Failed to update voucher', 'Error');
        }
      });
    } else {
      const payload: any = {
        code: val.code.toUpperCase(),
        discountType: val.discountType,
        value: val.value,
        usageLimit: val.usageLimit || 0,
        description: val.description || ''
      };
      if (val.expiresAt) payload.expiresAt = new Date(val.expiresAt + 'T23:59:59').toISOString();
      this.loyaltyService.createChainVoucher(this.chainId, payload).subscribe({
        next: () => {
          this.voucherSubmitting = false;
          this.toastr.success('Voucher created', 'Created');
          this.closeVoucherDialog();
          this.loadVouchers();
        },
        error: (err: any) => {
          this.voucherSubmitting = false;
          this.toastr.error(err.error?.response?.message || 'Failed to create voucher', 'Error');
        }
      });
    }
  }

  deleteVoucher(voucherId: string): void {
    Swal.fire({
      title: 'Delete voucher?',
      text: 'This will permanently delete the chain voucher code.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, delete it',
      confirmButtonColor: '#dc3545'
    }).then(result => {
      if (result.isConfirmed) {
        this.loyaltyService.deleteChainVoucher(this.chainId, voucherId).subscribe({
          next: () => {
            this.toastr.success('Voucher deleted', 'Deleted');
            this.loadVouchers();
          },
          error: (err: any) => {
            this.toastr.error(err.error?.response?.message || 'Failed to delete voucher', 'Error');
          }
        });
      }
    });
  }

  toggleVoucherActive(voucher: any): void {
    const newActive = !voucher.isActive;
    this.loyaltyService.updateChainVoucher(this.chainId, voucher._id, { isActive: newActive }).subscribe({
      next: () => {
        voucher.isActive = newActive;
        this.toastr.success(newActive ? 'Voucher activated' : 'Voucher deactivated', 'Updated');
      },
      error: (err: any) => {
        this.toastr.error(err.error?.response?.message || 'Failed to update voucher', 'Error');
      }
    });
  }

  onVoucherCodeInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const upper = input.value.toUpperCase();
    this.voucherForm.patchValue({ code: upper }, { emitEvent: false });
    input.value = upper;
  }

  // ======================== Forms init ========================

  private initForms(): void {
    this.addMemberForm = new FormGroup({
      userSearch: new FormControl(''),
      userId: new FormControl('', [Validators.required]),
      tierKey: new FormControl('', [Validators.required]),
      expiresAt: new FormControl('')
    });

    this.editMemberForm = new FormGroup({
      tierKey: new FormControl('', [Validators.required]),
      expiresAt: new FormControl(''),
      isActive: new FormControl(true)
    });

    this.voucherForm = new FormGroup({
      code: new FormControl('', [Validators.required]),
      discountType: new FormControl('percentage', [Validators.required]),
      value: new FormControl(10, [Validators.required, Validators.min(0)]),
      usageLimit: new FormControl(100, [Validators.min(0)]),
      expiresAt: new FormControl(''),
      description: new FormControl(''),
      isActive: new FormControl(true)
    });
  }

  // ======================== Helpers ========================

  formatDate(iso: string | null): string {
    if (!iso) return '—';
    const d = new Date(iso);
    return d.toLocaleDateString('da-DK');
  }

  trackByIndex(index: number): number {
    return index;
  }

  trackById(index: number, item: any): string {
    return item._id;
  }
}
